import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useFilters } from "@/context/FiltersContext";
import { useAuth } from "@/context/AuthContext";
import useWatchlist from "@/lib/useWatchlist";
import { money, fmtDate, entryLink } from "@/lib/ui";
import { getChoices, pickPerf, toggleEnteredPerf } from "@/lib/choices";
import { unlockDraw } from "@/lib/drawLookup";
import { PickCircle } from "@/components/PickCircle";
import { OfficialBox } from "@/components/OfficialControls";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Info, PlusCircle, Users, ListOrdered, ArrowLeftRight, ExternalLink, ChevronDown, Globe, ScrollText, Star } from "lucide-react";
import { GroundRulesText } from "@/components/GroundRulesText";
import { ArenaCard, arenaInfo } from "@/components/ArenaCard";
import useTradeConflicts from "@/lib/useTradeConflicts";
import TradeFinderDialog from "@/components/TradeFinderDialog";
import PurseBreakdown, { useEnteredCounts } from "@/components/PurseBreakdown";

function groundRulesText(pbj, rodeo) {
  const gr = pbj?.ground_rules || [];
  const listing = pbj?.listing;
  return (listing?.parsed?.ground_rules) || (gr[0]?.parsed?.ground_rules) || (gr[0]?.raw_text) || rodeo?.ground_rules || "";
}

const DLG = "bg-[#161B22] text-slate-100 border border-white/10 w-[calc(100vw-1rem)] max-w-lg max-h-[88vh] overflow-y-auto overflow-x-hidden";
const isExternal = (r) => !!(r.prca || r.cca || r.source === "prca" || r.source === "cca");

// Shared action row used across mobile Table / Calendar / Map so every rodeo has the same buttons.
export function MRodeoActions({ r, setDlg, className = "" }) {
  const ext = isExternal(r);
  const st = r.status;
  const { user } = useAuth();
  const { isWatched, toggle } = useWatchlist();
  const on = isWatched(r.id);
  const conflict = useTradeConflicts().conflictFor(r.id);
  return (
    <div className={`flex flex-nowrap items-center gap-1.5 overflow-x-auto no-scrollbar ${className}`} data-testid={`m-actions-${r.id}`}>
      {user && (
        <button onClick={(e) => { e.stopPropagation(); toggle(r.id, r.name); }} data-testid={`m-watch-${r.id}`}
          className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold ${on ? "bg-[#C8000F] text-white" : "bg-white/5 text-slate-300"}`}>
          <Star className={`w-4 h-4 ${on ? "fill-white" : ""}`} /> {on ? "Watching" : "Watch"}
        </button>
      )}
      <ActBtn onClick={() => setDlg({ type: "info", rodeo: r })} icon={Info} label="Info" testid={`m-info-${r.id}`} />
      {st !== "closed" && <ActBtn onClick={() => { window.open(entryLink(r), "_blank"); }} icon={PlusCircle} label="Enter" testid={`m-enter-${r.id}`} primary />}
      {!ext && st === "open" && <ActBtn onClick={() => setDlg({ type: "count", rodeo: r })} icon={Users} label="Count" testid={`m-count-${r.id}`} />}
      <ActBtn onClick={() => setDlg({ type: "draw", rodeo: r })} icon={ListOrdered} label="Draw" testid={`m-draw-${r.id}`} />
      {conflict && <ActBtn onClick={() => setDlg({ type: "trade", rodeo: r, conflict })} icon={ArrowLeftRight} label="Trade" testid={`m-trade-${r.id}`} primary={!!conflict.conflicts_with?.length} />}
      <OfficialBox rodeoId={r.id} dark className="shrink-0" />
    </div>
  );
}

export function MRodeoDialogs({ dlg, onClose }) {
  return (
    <>
      <InfoDialog data={dlg?.type === "info" ? dlg.rodeo : null} onClose={onClose} />
      <CountDialog data={dlg?.type === "count" ? dlg.rodeo : null} onClose={onClose} />
      <DrawDialog data={dlg?.type === "draw" ? dlg.rodeo : null} onClose={onClose} />
      <TradeFinderDialog rodeo={dlg?.type === "trade" ? dlg.rodeo : null} conflict={dlg?.conflict} onClose={onClose} dark />
    </>
  );
}

export function ActBtn({ onClick, icon: Icon, label, testid, primary }) {
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }} data-testid={testid}
      className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold ${primary ? "bg-[#C8000F] text-white" : "bg-white/5 text-slate-300"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function MInfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="rounded-md bg-white/5 px-3 py-2 min-w-0">
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm text-slate-100 font-medium mt-0.5 break-words">{value}</div>
    </div>
  );
}

function MMoneyTable({ title, rows, testid }) {
  if (!rows || !rows.length) return null;
  return (
    <div className="rounded-md border border-white/10 p-3" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-2">{title}</div>
      <div className="space-y-1">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between text-sm py-1 px-2 rounded bg-white/5">
            <span className="text-slate-300">{r.event}</span>
            <span className="text-white font-semibold">{money(r.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function InfoDialog({ data, onClose }) {
  const { user } = useAuth();
  const { isWatched, toggle } = useWatchlist();
  const [showRaw, setShowRaw] = useState(false);
  const [section, setSection] = useState("details");
  const [pbj, setPbj] = useState(null);
  const [pbjLoading, setPbjLoading] = useState(false);
  const [choices, setChoices] = useState(getChoices());
  const entered = useEnteredCounts(data?.id);
  useEffect(() => {
    if (data) {
      setShowRaw(false); setSection("details"); setPbj(null); setChoices(getChoices());
      setPbjLoading(true);
      api.get(`/prca/rodeo/${data.id}/pbj`).then((r) => setPbj(r.data)).catch(() => setPbj(null)).finally(() => setPbjLoading(false));
    }
  }, [data]);
  const perfs = data?.performances || [];
  const ch = (data && choices[data.id]) || {};
  const _arena = arenaInfo(pbj);
  const _arenaName = data?.arena || _arena?.name || "";
  const _arenaAddr = _arena?.address ? [_arena.address, _arena.location].filter(Boolean).join(", ") : null;
  const _arenaMaps = _arenaName ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([_arenaName, _arena?.address, _arena?.location].filter(Boolean).join(", "))}` : null;
  const arenaNode = _arenaName ? (
    <span>
      {_arenaName}
      {_arenaAddr && <span className="block text-[12px] text-slate-400 font-normal mt-0.5" data-testid="m-info-arena-address">{_arenaAddr}</span>}
      {_arenaAddr && <a href={_arenaMaps} target="_blank" rel="noreferrer" data-testid="m-info-arena-directions" className="inline-flex items-center gap-1 mt-1 text-[12px] font-semibold text-[#F26D77]">Get directions</a>}
    </span>
  ) : null;
  return (
    <Dialog open={!!data} onOpenChange={onClose}>
      <DialogContent className={DLG} data-testid="m-info-dialog">
        {data && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-white">{data.name}</DialogTitle>
              <DialogDescription className="text-slate-400">How to enter, ground rules & schedule</DialogDescription>
            </DialogHeader>
            <div className="flex flex-nowrap gap-2 pt-1 overflow-x-auto no-scrollbar" data-testid="m-info-actions">
              {user && (
                <button onClick={() => toggle(data.id, data.name)} data-testid="m-info-watch"
                  className={`shrink-0 flex items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold py-2.5 px-4 rounded-lg transition-colors ${isWatched(data.id) ? "bg-[#C8000F] text-white" : "bg-white/10 border border-white/15 text-slate-100 active:bg-white/15"}`}>
                  <Star className={`w-4 h-4 shrink-0 ${isWatched(data.id) ? "fill-white" : ""}`} /> {isWatched(data.id) ? "Watching" : "Watch"}
                </button>
              )}
              <a href={entryLink(data)} target="_blank" rel="noreferrer" data-testid="m-info-enter-link"
                className="shrink-0 flex items-center justify-center gap-2 whitespace-nowrap bg-[#C8000F] text-white text-sm font-semibold py-2.5 px-4 rounded-lg active:opacity-90">
                <ExternalLink className="w-4 h-4 shrink-0" /> {(data.associations || []).includes("CPRA") ? "Enter on CPRA" : (data.associations || []).includes("PRCA") ? "Enter on PRCA" : "Enter online"}
              </a>
              {data.website_url && (
                <a href={data.website_url} target="_blank" rel="noreferrer" data-testid="m-info-official-link"
                  className="shrink-0 flex items-center justify-center gap-2 whitespace-nowrap bg-white/10 border border-white/15 text-slate-100 text-sm font-semibold py-2.5 px-4 rounded-lg active:bg-white/15">
                  <Globe className="w-4 h-4 shrink-0" /> Official page
                </a>
              )}
            </div>
            <div className="flex gap-1.5 border-b border-white/10 pt-1" data-testid="m-info-section-tabs">
              {[["details", "Details"], ["rules", "Ground Rules"]].map(([k, lbl]) => (
                <button key={k} onClick={() => setSection(k)} data-testid={`m-info-section-${k}`}
                  className={`text-sm font-bold px-3 py-2 -mb-px border-b-2 transition-colors ${section === k ? "border-[#C8000F] text-[#F26D77]" : "border-transparent text-slate-400"}`}>
                  {lbl}
                </button>
              ))}
            </div>
            {section === "rules" ? (
              <div className="space-y-3 min-w-0" data-testid="m-info-rules-panel">
                <ArenaCard pbj={pbj} dark />
                {pbjLoading && !pbj ? (
                  <div className="flex items-center gap-2 text-sm text-slate-400 py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading ground rules…</div>
                ) : groundRulesText(pbj, data) ? (
                  <>
                    <div className="rounded-md border border-white/10 p-3">
                      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1.5"><ScrollText className="w-3.5 h-3.5" /> Ground rules</div>
                      <GroundRulesText text={groundRulesText(pbj, data)} dark />
                    </div>
                    {pbj?.source && (pbj?.ground_rules?.length || pbj?.listing?.parsed?.ground_rules) && (
                      <div className="text-[11px] text-slate-500">Source: {pbj.source}</div>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-slate-400 py-8 text-center" data-testid="m-info-rules-empty">No ground rules posted yet for this rodeo. They're pulled from the PRCA PBJ listing and appear here once published.</div>
                )}
              </div>
            ) : (
            <div className="space-y-3 min-w-0">
              <div className="grid grid-cols-2 gap-2 min-w-0">
                <MInfoRow label="Location" value={[data.city, data.state].filter(Boolean).join(", ")} />
                <MInfoRow label="Associations" value={(data.associations || []).join(", ")} />
                <MInfoRow label="Added money (total)" value={data.added_money ? money(data.added_money) : ""} />
                <MInfoRow label="Total entered" value={entered?.total ? String(entered.total) : ""} />
                <MInfoRow label="Arena" value={arenaNode} />
                <MInfoRow label="Entries open" value={fmtDate(data.entry_open)} />
                <MInfoRow label="Entries close" value={fmtDate(data.entry_close)} />
                <MInfoRow label="Committee contact" value={data.committee_contact} />
                <MInfoRow label="Rodeo office" value={data.rodeo_office} />
                <MInfoRow label="Stock contractor" value={data.main_stock} />
                <MInfoRow label="Timed-event stock" value={data.timed_stock} />
              </div>
              {(data.entry_info || data.how_to_enter) && (
                <div className="rounded-md border border-white/10 p-3" data-testid="m-info-how-to-enter">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">How to enter</div>
                  <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{data.entry_info || data.how_to_enter}</p>
                </div>
              )}
              <PurseBreakdown rodeo={data} entered={entered} dark testid="m-info-purses" />
              <MMoneyTable title="Entry fees by event" rows={data.entry_fees} testid="m-info-entry-fees" />
              {perfs.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Performances & slack</div>
                    <div className="text-[10px] text-slate-500">Tap <span className="text-[#C8000F] font-bold">⊕</span> to set 1st / 2nd</div>
                  </div>
                  {perfs.map((p) => {
                    const num = ch.pref1 === p.id ? 1 : ch.pref2 === p.id ? 2 : null;
                    const ent = !!(ch.entered || {})[p.id];
                    return (
                      <div key={p.id} data-testid={`m-info-perf-row-${p.id}`} className={`flex items-center justify-between gap-2 text-sm py-1.5 px-3 rounded-md min-w-0 ${ent ? "bg-[#16A34A]/15 ring-1 ring-[#16A34A]/50" : num === 1 ? "bg-[#166534]/15 ring-1 ring-[#166534]/40" : "bg-white/5"}`}>
                        <span className="flex items-center gap-2.5 min-w-0">
                          <PickCircle num={num} entered={ent} onClick={() => setChoices(pickPerf(data.id, p.id))} onToggleEntered={() => setChoices(toggleEnteredPerf(data.id, p.id))} testid={`m-info-pick-${p.id}`} />
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${p.type === "slack" ? "bg-purple-500/20 text-purple-300" : "bg-blue-500/20 text-blue-300"}`}>{p.type === "slack" ? "SLACK" : "PERF"}</span>
                          <span className="truncate">{p.label}</span>
                        </span>
                        <span className="text-slate-400 shrink-0 pl-2">{fmtDate(p.date)}{p.time ? ` · ${p.time}` : ""}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {(data.perf_order || data.slack_order) && (
                <div className="rounded-md bg-white/5 px-3 py-2 text-sm text-slate-300 space-y-1" data-testid="m-info-order">
                  {data.perf_order && <div><span className="font-semibold text-slate-400">Performance order:</span> {data.perf_order}</div>}
                  {data.slack_info && <div><span className="font-semibold text-slate-400">Slack:</span> {data.slack_info}</div>}
                  {data.slack_order && <div><span className="font-semibold text-slate-400">Slack order:</span> {data.slack_order}</div>}
                </div>
              )}
              {data.ground_rules && (
                <div className="rounded-md border border-white/10 p-3" data-testid="m-info-ground-rules">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Ground rules</div>
                  <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line line-clamp-6">{data.ground_rules}</p>
                  <button onClick={() => setSection("rules")} data-testid="m-info-view-rules" className="mt-2 text-xs font-semibold text-[#F26D77]">View full ground rules →</button>
                </div>
              )}
              {data.details_text && (
                <div className="rounded-md border border-white/10" data-testid="m-info-full-details">
                  <button onClick={() => setShowRaw((v) => !v)} data-testid="m-info-full-details-toggle"
                    className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-semibold text-slate-200">
                    <span>Full rodeo details</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showRaw ? "rotate-180" : ""}`} />
                  </button>
                  {showRaw && <p className="px-3 pb-3 text-sm text-slate-300 leading-relaxed whitespace-pre-line border-t border-white/10 pt-2">{data.details_text}</p>}
                </div>
              )}
            </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DrawDialog({ data, onClose }) {
  const nav = useNavigate();
  const [draw, setDraw] = useState(null);
  const [locked, setLocked] = useState(false);
  useEffect(() => {
    if (!data) return;
    setDraw(null); setLocked(false);
    unlockDraw(data.id).then((res) => {
      if (!res.ok) { setLocked(true); return; }
      api.get(`/rodeos/${data.id}/draw`).then((r) => setDraw(r.data)).catch(() => setDraw({ real_draw: false, events: [] }));
    });
  }, [data]);
  return (
    <Dialog open={!!data} onOpenChange={onClose}>
      <DialogContent className={DLG} data-testid="m-draw-dialog">
        {data && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-white">Draw · {data.name}</DialogTitle>
              <DialogDescription className="text-slate-400">Who's up, by event and performance</DialogDescription>
            </DialogHeader>
            {locked ? (
              <div className="text-center py-8 px-4" data-testid="m-draw-paywall">
                <p className="text-sm text-slate-300">You've used your free draw lookup. Unlock this draw with 1 credit, or subscribe for unlimited.</p>
                <button onClick={() => { onClose(); nav("/choose-plan"); }} data-testid="m-draw-see-plans" className="mt-3 bg-[#C8000F] text-white text-sm font-semibold px-4 py-2.5 rounded-lg">See plans</button>
              </div>
            ) : !draw ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>
              : !draw.real_draw || !(draw.events || []).length ? (
                <p className="text-sm text-slate-400 py-4">The draw hasn't been posted yet for this rodeo. Check back closer to the performance dates.</p>
              ) : (
                <div className="space-y-3 max-h-[55vh] overflow-y-auto">
                  {draw.events.map((ev) => (
                    <div key={ev.event} className="rounded-lg border border-white/10 p-3">
                      <div className="text-sm font-semibold mb-2 flex items-center justify-between text-slate-100"><span>{ev.event}</span><span className="text-xs text-slate-500">{ev.total} entered</span></div>
                      <div className="space-y-1">
                        {ev.rows.slice(0, 60).map((r, i) => (
                          <div key={i} className="flex items-center justify-between text-xs py-1 px-2 rounded bg-white/5">
                            <span className="text-slate-200"><b>{r.position}.</b> {ev.team ? `${r.header || "?"} / ${r.heeler || "?"}` : r.contestant}</span>
                            <span className="text-slate-400 text-right">{r.performance_label}{r.stock ? ` · ${r.stock}` : ""}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            <button onClick={() => { onClose(); nav(`/rodeo/${data.id}`); }} data-testid="m-draw-open-full"
              className="w-full mt-3 border border-white/15 text-slate-200 text-sm font-semibold py-2.5 rounded-lg">Open full draw sheet</button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}


function CountDialog({ data, onClose }) {
  const { events: myEvents } = useFilters();
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    if (data) { setCounts(null); api.get(`/rodeos/${data.id}/count`).then((r) => setCounts(r.data)).catch(() => setCounts({})); }
  }, [data]);
  const evList = data ? (data.events || []).filter((ev) => myEvents.size === 0 || myEvents.has(ev)) : [];
  return (
    <Dialog open={!!data} onOpenChange={onClose}>
      <DialogContent className={DLG} data-testid="m-count-dialog">
        {data && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-white">Entry Count · {data.name}</DialogTitle>
              <DialogDescription className="text-slate-400">How the field is filling up per performance.</DialogDescription>
            </DialogHeader>
            {!counts ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div> : (
              <div className="space-y-3 max-h-[55vh] overflow-y-auto">
                {(data.performances || []).map((p) => (
                  <div key={p.id} className="rounded-lg border border-white/10 p-3">
                    <div className="text-sm font-semibold mb-2 flex items-center justify-between text-slate-100"><span>{p.label}</span><span className="text-xs text-slate-500">{fmtDate(p.date)}</span></div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {evList.map((ev) => (
                        <div key={ev} className="flex items-center justify-between text-xs px-2 py-1 rounded bg-white/5">
                          <span className="text-slate-400 truncate mr-2">{ev}</span>
                          <span className="text-[#F26D77] font-bold">{counts[p.id]?.[ev] ?? 0}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
